import React from "react";
import { Button, Column, Icon, Row, Select, SelectOption, Text } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import cameraTokens from "@amzn/meridian-tokens/base/icon/camera";
import { compliantStatus, NON_COMPLIANT } from "src/constants/constants";
import ImageUploader from "src/components/UI/imageUploader";

interface ManualOverrideSheetProps {
  overrideResult: string;
  reasonCode: string;
  evidenceFile: File | string;
  aiResult: string;
  onResultChange: (result: string) => void;
  onReasonChange: (reason: string) => void;
  onImageUpload: (file: File) => void;
  onSave: () => void;
  onCancel: () => void;
  disabled?: boolean;
}

/**
 * Sheet component for manual override of AI results
 * Allows inspector to override AI decision with reason and evidence
 */
const ManualOverrideSheet: React.FC<ManualOverrideSheetProps> = ({
  overrideResult,
  reasonCode,
  evidenceFile,
  aiResult,
  onResultChange,
  onReasonChange,
  onImageUpload,
  onSave,
  onCancel,
  disabled = false,
}) => {
  const isSaveDisabled = !evidenceFile || !overrideResult || !reasonCode || disabled;

  return (
    <Column>
      <Row alignmentHorizontal="justify">
        <Text type="h400">Manual Override</Text>
        <Button onClick={onCancel} type="icon">
          <Icon tokens={closeSmallTokens} />
        </Button>
      </Row>
      
      <Column spacing={"200"}>
        <Text>Select Answer</Text>
        <Row widths={"fill"}>
          <Button
            onClick={() => onResultChange(compliantStatus.YES)}
            type={overrideResult === compliantStatus.YES ? "primary" : "secondary"}
            disabled={aiResult !== NON_COMPLIANT}
          >
            Yes (Accessible)
          </Button>
          <Button
            onClick={() => onResultChange("No")}
            type={overrideResult === "No" ? "primary" : "secondary"}
            disabled={aiResult === NON_COMPLIANT}
          >
            Not Accessible
          </Button>
        </Row>
      </Column>
      
      <Column spacing={"200"}>
        <Text>Reason Code</Text>
        <Select
          value={reasonCode}
          onChange={onReasonChange}
          placeholder="Choose Reason"
        >
          <SelectOption value="puppy" label="Puppy" />
          <SelectOption value="cat" label="Cat" />
          <SelectOption value="bunny" label="Bunny" />
        </Select>
      </Column>
      
      <Column spacing={"200"}>
        <Text>Attach Image Evidence</Text>
        <Row alignmentHorizontal="justify" widths={"fill"}>
          <Button type="secondary">
            <Icon tokens={cameraTokens} />
            Capture
          </Button>
          <ImageUploader
            handleImageUpload={onImageUpload}
            draggable={false}
          />
        </Row>
      </Column>
      
      <Row
        alignmentHorizontal="justify"
        widths={"fill"}
        spacingInset={"400 none none"}
      >
        <Button onClick={onCancel} type="tertiary">
          Cancel
        </Button>
        <Button onClick={onSave} type="primary" disabled={isSaveDisabled}>
          Save and Continue
        </Button>
      </Row>
    </Column>
  );
};

export default ManualOverrideSheet;
